import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";

// Import resources
import airflowP2s from "../../../utils/resources/KK_P_Left_Top_sCrop.svg";
import airflowP4s from "../../../utils/resources/KK_P_Right_Top_sCrop.svg";
import airflowR1s from "../../../utils/resources/KK_R_Left_Bottom_sCrop.svg";
import airflowR3s from "../../../utils/resources/KK_R_Right_Bottom_sCrop.svg";
import airflowR4s from "../../../utils/resources/KK_R_Right_Top_s-cropped.svg";
import airflowR2s from "../../../utils/resources/KK_R_Left_Top_s-cropped.svg";
import airflowE2s from "../../../utils/resources/KK_E_Left_Top_sCrop.svg";
import airflowE4s from "../../../utils/resources/KK_E_Right_Top_sCrop.svg";
import verticalAHU1Unselected from "../../../utils/resources/Vertical_AHU_1_unselected.svg";
import verticalAHU2Unselected from "../../../utils/resources/Vertical_AHU_2_unselected.svg";
import frameDonja from "./FrameDonja.svg";
import kucicaLeto from "./kucicaLeto.svg";
import Coils from "./Coils";
import { useParams } from "react-router-dom";
import { AhuType } from "../../../models/unitType";

// MUI components
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

const OperatingPoint1 = () => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const params = useParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;
  const isLinePrime = baseUnitType === AhuType.LINE_PRIME;
  console.log("params", params);

  // Local state for toggle switches
  const [summerManualEdit, setSummerManualEdit] = useState(false);
  const [winterManualEdit, setWinterManualEdit] = useState(false);

  // Watch form values
  const coils = watch("coils");
  const unitType = watch("Type");
  const heatRecovery = watch("heatRecovery");
  const heatRecoveryPlateType = watch("heatRecoveryPlateType");
  const airflowDirection = watch("airflowDirection");
  const heaterType = watch("heaterType");
  const heaterConcentration = watch("heaterConcentration");
  const heaterTemperatureIn = watch("heaterTemperatureIn");
  const heaterTemperatureOut = watch("heaterTemperatureOut");
  const coolerType = watch("coolerType");
  const coolerConcentration = watch("coolerConcentration");
  const coolerTemperatureIn = watch("coolerTemperatureIn");
  const coolerTemperatureOut = watch("coolerTemperatureOut");

  // Determine image path based on heat recovery and airflow direction
  const imgpath = useMemo(() => {
    if (heatRecovery === "Rotary heat exchanger" && airflowDirection === 1)
      return airflowR1s;
    else if (heatRecovery === "Rotary heat exchanger" && airflowDirection === 2)
      return airflowR3s;
    else if (heatRecovery === "Rotary heat exchanger" && airflowDirection === 3)
      return airflowR2s;
    else if (heatRecovery === "Rotary heat exchanger" && airflowDirection === 4)
      return airflowR4s;
    else if (
      heatRecovery === "Polypropylene with indirect adiabatic cooling" &&
      airflowDirection === 1
    )
      return airflowP2s;
    else if (
      heatRecovery === "Polypropylene with indirect adiabatic cooling" &&
      airflowDirection === 2
    )
      return airflowP4s;
    else if (
      heatRecovery === "Plate heat exchanger" &&
      unitType === AhuType.COMPACT_VERTICAL &&
      airflowDirection === 1
    )
      return verticalAHU1Unselected;
    else if (
      heatRecovery === "Plate heat exchanger" &&
      unitType === AhuType.COMPACT_VERTICAL &&
      airflowDirection === 2
    )
      return verticalAHU2Unselected;
    else if (
      heatRecovery === "Plate heat exchanger" &&
      heatRecoveryPlateType === "Plate" &&
      airflowDirection === 1
    )
      return airflowP2s;
    else if (
      heatRecovery === "Plate heat exchanger" &&
      heatRecoveryPlateType === "Plate" &&
      airflowDirection === 2
    )
      return airflowP4s;
    else if (
      heatRecovery === "Plate heat exchanger" &&
      heatRecoveryPlateType === "Enthalpy" &&
      airflowDirection === 1
    )
      return airflowE2s;
    else if (
      heatRecovery === "Plate heat exchanger" &&
      heatRecoveryPlateType === "Enthalpy" &&
      airflowDirection === 2
    )
      return airflowE4s;
    return null;
  }, [heatRecovery, heatRecoveryPlateType, airflowDirection, unitType]);

  const imgStyle = unitType === AhuType.COMPACT_VERTICAL
    ? { width: "10em", height: "auto" }
    : {};

  // Validation rules
  const validationRules = {
    summerOutdoorTemperature: {
      required: t("operatingpoint.err") || "This field is required",
    },
    summerWetBulbTemperature: {},
    summerDewPointTemperature: {},
    summerOutdoorHumidity: {
      required: t("operatingpoint.err") || "This field is required",
    },
    summerIndoorTemperature: {
      required: t("operatingpoint.err") || "This field is required",
    },
    summerIndoorHumidity: {
      required: t("operatingpoint.err") || "This field is required",
    },
    winterOutdoorTemperature: {
      required: t("operatingpoint.err") || "This field is required",
    },
    winterOutdoorHumidity: {
      required: t("operatingpoint.err") || "This field is required",
    },
    winterIndoorTemperature: {
      required: t("operatingpoint.err") || "This field is required",
    },
    winterIndoorHumidity: {
      required: t("operatingpoint.err") || "This field is required",
    },
    elevation: {},
  };

  return (
    <Container
      component="main"
      sx={{ mb: 4, "min-width": "100%", width: "100%" }}
      className="test"
    >
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 5 },
          align: "center",
        }}
      >
        <Box
          className="test"
          sx={{
            display: { xl: "flex", xs: "block", gap: "20px" },
          }}
        >
          {/* Summer Section */}
          <Box
            sx={{
              width: { xs: "100%", xl: "100%" },
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", xl: "40%" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box>
                  <Typography
                    component="h2"
                    variant="h4"
                    sx={{ margin: { xs: "0 0 10px 10px", sm: "16px", xl: 2 } }}
                  >
                    {t("operatingpoint.summer")}
                  </Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={summerManualEdit}
                        onChange={() => setSummerManualEdit(!summerManualEdit)}
                      />
                    }
                    label={"Manual input"}
                    sx={{ mb: 1, ml: { xs: 1, sm: 2 } }}
                  />
                  <Box sx={{ display: "block" }}>
                    <Box
                      className="provera"
                      sx={{
                        display: { xl: "flex", xs: "block" },
                        width: { xl: "100%", xs: "100%" },
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "5px",
                        }}
                      >
                        <Box
                          sx={{
                            width: { xl: "50%", xs: "100%" },
                            mb: { xl: 0, xs: 0 },
                          }}
                        >
                          <Tooltip
                            title={t(
                              "operatingpoint.dryBulbTemperatureCelsius"
                            )}
                            placement="top"
                          >
                            <Controller
                              name="summerOutdoorTemperature"
                              control={control}
                              rules={validationRules.summerOutdoorTemperature}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  {...field}
                                  disabled={!summerManualEdit}
                                  fullWidth
                                  id="summerOutdoorTemperature"
                                  label={t(
                                    "operatingpoint.dryBulbTemperatureCelsius"
                                  )}
                                  type="number"
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 0.1 }}
                                  helperText={error?.message}
                                  error={!!error}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="start">
                                        °C
                                      </InputAdornment>
                                    ),
                                    onWheel: (e) => e.target.blur(),
                                  }}
                                />
                              )}
                            />
                          </Tooltip>
                        </Box>

                        <Box
                          sx={{
                            width: { xl: "50%", xs: "100%" },
                            mb: { xl: 1, xs: 0 },
                          }}
                        >
                          <Tooltip
                            title={t(
                              "operatingpoint.wetBulbTemperatureCelsius"
                            )}
                            placement="top"
                          >
                            <Controller
                              name="summerWetBulbTemperature"
                              control={control}
                              rules={validationRules.summerWetBulbTemperature}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  disabled
                                  id="summerWetBulbTemperature"
                                  label={t(
                                    "operatingpoint.wetBulbTemperatureCelsius"
                                  )}
                                  type="number"
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 0.1 }}
                                  helperText={error?.message}
                                  error={!!error}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="start">
                                        °C
                                      </InputAdornment>
                                    ),
                                    onWheel: (e) => e.target.blur(),
                                  }}
                                />
                              )}
                            />
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "5px",
                        }}
                      >
                        <Box
                          sx={{
                            width: { xl: "100%", xs: "100%" },
                            mb: { xl: 1, xs: 0 },
                          }}
                        >
                          <Tooltip
                            title={t(
                              "operatingpoint.dewPointTemperatureCelsius"
                            )}
                            placement="top"
                          >
                            <Controller
                              name="summerDewPointTemperature"
                              control={control}
                              rules={validationRules.summerDewPointTemperature}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  disabled
                                  id="summerDewPointTemperature"
                                  label={t(
                                    "operatingpoint.dewPointTemperatureCelsius"
                                  )}
                                  type="number"
                                  InputLabelProps={{ shrink: true }}
                                  helperText={error?.message}
                                  error={!!error}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="start">
                                        °C
                                      </InputAdornment>
                                    ),
                                    onWheel: (e) => e.target.blur(),
                                  }}
                                />
                              )}
                            />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            width: { xl: "100%", xs: "100%" },
                            mt: { sm: 0, xl: 0 },
                          }}
                        >
                          <Tooltip
                            title={t("operatingpoint.relativeHumiditySummer")}
                            placement="top"
                          >
                            <Controller
                              name="summerOutdoorHumidity"
                              control={control}
                              rules={validationRules.summerOutdoorHumidity}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  disabled={!summerManualEdit}
                                  id="summerOutdoorHumidity"
                                  label={t(
                                    "operatingpoint.relativeHumiditySummer"
                                  )}
                                  type="number"
                                  InputLabelProps={{ shrink: true }}
                                  helperText={error?.message}
                                  error={!!error}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="start">
                                        %
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Mobile view - indoor fields */}
                      <Box
                        sx={{
                          display: { xs: "block", md: "none" },
                        }}
                      >
                        <Controller
                          name="summerIndoorTemperature"
                          control={control}
                          rules={validationRules.summerIndoorTemperature}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              fullWidth
                              id="summerIndoorTemperature"
                              label={t(
                                "operatingpoint.summerIndoorTemperature"
                              )}
                              type="number"
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 0.1 }}
                              helperText={error?.message}
                              error={!!error}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="start">
                                    °C
                                  </InputAdornment>
                                ),
                                onWheel: (e) => e.target.blur(),
                              }}
                            />
                          )}
                        />
                        <Controller
                          name="summerIndoorHumidity"
                          control={control}
                          rules={validationRules.summerIndoorHumidity}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              fullWidth
                              id="summerIndoorHumidity"
                              label={t("operatingpoint.summerIndoorHumidity")}
                              type="number"
                              InputLabelProps={{ shrink: true }}
                              helperText={error?.message}
                              error={!!error}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="start">
                                    %
                                  </InputAdornment>
                                ),
                                onWheel: (e) => e.target.blur(),
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              {/* Airflow direction image */}
              {!isLinePrime && (
              <Box
                sx={{
                  justifySelf: "flex-end",
                  position: "relative",
                  left: "40px",
                  zIndex: 1,
                  display: { xs: "none", md: "block" },
                }}
              >
                {imgpath && <img alt="Airflow direction" src={imgpath} style={imgStyle} />}
              </Box>
              )}
            </Box>
            {/* Desktop view - house image with indoor fields */}
            <Box
              sx={{
                width: { xs: "100%", xl: "60%" },
                position: "relative",
                display: { xs: "none", md: "flex" },
              }}
            >
              <img
                src={kucicaLeto}
                alt="kucica"
                style={{    
                  width: "100%",
                  objectFit: "contain",
                }}
              />
              <Box
                sx={{
                  width: "60%",
                  position: "absolute",
                  bottom: "50%",
                  left: "50%",
                  transform: "translate(-50%,90%)",
                  alignSelf: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <Controller
                  name="summerIndoorTemperature"
                  control={control}
                  rules={validationRules.summerIndoorTemperature}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id="summerIndoorTemperature"
                      label={t("operatingpoint.summerIndoorTemperature")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 0.1 }}
                      helperText={error?.message}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">°C</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  )}
                />
                <Controller
                  name="summerIndoorHumidity"
                  control={control}
                  rules={validationRules.summerIndoorHumidity}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id="summerIndoorHumidity"
                      label={t("operatingpoint.summerIndoorHumidity")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      helperText={error?.message}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Winter Section */}
          <Box
            sx={{
              width: { xs: "100%", xl: "100%" },
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", xl: "40%" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box>
                  <Typography
                    component="h2"
                    variant="h4"
                    sx={{ margin: { xs: "0 0 10px 10px", sm: "16px", xl: 2 } }}
                  >
                    {t("operatingpoint.winter")}
                  </Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={winterManualEdit}
                        onChange={() => setWinterManualEdit(!winterManualEdit)}
                      />
                    }
                    label={"Manual input"}
                    sx={{ mb: 1, ml: { xs: 1, sm: 2 } }}
                  />
                  <Box sx={{ display: "block" }}>
                    <Box
                      sx={{
                        display: { xl: "flex", xs: "block" },
                        width: { xl: "100%", xs: "100%" },
                        gap: "5px",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xl: "100%", xs: "100%" },
                          mb: { xl: 1, xs: 0 },
                        }}
                      >
                        <Tooltip
                          title={t("operatingpoint.heatingDB")}
                          placement="top"
                        >
                          <Controller
                            name="winterOutdoorTemperature"
                            control={control}
                            rules={validationRules.winterOutdoorTemperature}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                fullWidth
                                disabled={!winterManualEdit}
                                id="winterOutdoorTemperature"
                                label={t("operatingpoint.heatingDB")}
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 0.1 }}
                                helperText={error?.message}
                                error={!!error}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="start">
                                      °C
                                    </InputAdornment>
                                  ),
                                  onWheel: (e) => e.target.blur(),
                                }}
                              />
                            )}
                          />
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          width: { xl: "100%", xs: "100%" },
                          mt: { sm: 1, xl: 0 },
                        }}
                      >
                        <Tooltip
                          title={t("operatingpoint.relativeHumidityWinter")}
                          placement="top"
                        >
                          <Controller
                            name="winterOutdoorHumidity"
                            control={control}
                            rules={validationRules.winterOutdoorHumidity}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                fullWidth
                                disabled={!winterManualEdit}
                                id="relativeHumidityWinter"
                                label={t(
                                  "operatingpoint.relativeHumidityWinter"
                                )}
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                helperText={error?.message}
                                error={!!error}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="start">
                                      %
                                    </InputAdornment>
                                  ),
                                  onWheel: (e) => e.target.blur(),
                                }}
                              />
                            )}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Mobile view - indoor fields */}
                    <Box
                      sx={{
                        alignSelf: "flex-end",
                        flexDirection: "column",
                        gap: "15px",
                        display: { xs: "flex", md: "none" },
                      }}
                    >
                      <Controller
                        name="winterIndoorTemperature"
                        control={control}
                        rules={validationRules.winterIndoorTemperature}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="winterIndoorTemperature"
                            label={t("operatingpoint.winterIndoorTemperature")}
                            type="number"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 0.1 }}
                            helperText={error?.message}
                            error={!!error}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="start">
                                  °C
                                </InputAdornment>
                              ),
                              onWheel: (e) => e.target.blur(),
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="winterIndoorHumidity"
                        control={control}
                        rules={validationRules.winterIndoorHumidity}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="winterIndoorHumidity"
                            label={t("operatingpoint.winterIndoorHumidity")}
                            type="number"
                            InputLabelProps={{ shrink: true }}
                            helperText={error?.message}
                            error={!!error}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="start">
                                  %
                                </InputAdornment>
                              ),
                              onWheel: (e) => e.target.blur(),
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              {/* Airflow direction image */}
              {!isLinePrime && (
              <Box
                sx={{
                  justifySelf: "flex-end",
                  position: "relative",
                  left: "40px",
                  zIndex: 1,
                  display: { xs: "none", md: "block" },
                }}
              >
                {imgpath && <img alt="Airflow direction" src={imgpath} style={imgStyle} />}
              </Box>
              )}
            </Box>
            {/* Desktop view - house image with indoor fields */}
            <Box
              sx={{
                width: { xs: "100%", xl: "60%" },
                position: "relative",
                display: { xs: "none", md: "flex" },
              }}
            >
              <img
                src={frameDonja}
                alt="kucica"
                style={{
                  width: "100%",
                  objectFit: "contain",
                }}
              />
              <Box
                sx={{
                  width: "60%",
                  position: "absolute",
                  bottom: "50%",
                  left: "50%",
                  transform: "translate(-50%,90%)",
                  alignSelf: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <Controller
                  name="winterIndoorTemperature"
                  control={control}
                  rules={validationRules.winterIndoorTemperature}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id="winterIndoorTemperature"
                      label={t("operatingpoint.winterIndoorTemperature")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 0.1 }}
                      helperText={error?.message}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">°C</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  )}
                />
                <Controller
                  name="winterIndoorHumidity"
                  control={control}
                  rules={validationRules.winterIndoorHumidity}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id="winterIndoorHumidity"
                      label={t("operatingpoint.winterIndoorHumidity")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      helperText={error?.message}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Elevation field */}
        <Box
          sx={{
            width: { xl: "30%", xs: "100%" },
            height: "60px",
            marginTop: "35px",
          }}
        >
          <Tooltip title={t("operatingpoint.elevation")} placement="top">
            <Controller
              name="elevation"
              control={control}
              rules={validationRules.elevation}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled
                  id="elevation"
                  label={t("operatingpoint.elevation")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">m</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Tooltip>
        </Box>
      </Paper>
    </Container>
  );
};

export default OperatingPoint1;
